import { GetSchools } from "../graphql/queries/GetSchools";
import { isAuthError } from "../graphql/isAuthError";
import { rollbar } from "../loggers";

// The tablet pages read the student's school and classrooms from
// sessionStorage ("school" / "classroom"). Until now only the navbar wrote
// them, after its own GetSchools request, and Booksets polled for ten seconds
// and then gave up for good — so one slow or failed request (a fresh SSO
// login on 4G) left the page blank until a manual reload. Every reader now
// goes through here: whoever asks first fetches, with a retry, and writes the
// same keys; later callers share the request in flight or read what is stored.

const SCHOOL_KEY = "school";
const CLASSROOM_KEY = "classroom";
// Which account the stored school/classroom belong to. On a shared tablet
// the account can change without the tab being signed out first (the SSO
// handover reloads straight into /mybestuser?code=, and sessionStorage
// survives a reload), so data left by the previous student must not be
// taken for the current one.
const OWNER_KEY = "schoolClassroomOwner";

// One retry after a short pause, then a longer one: enough for a connection
// that is still waking up, short enough that the placeholders never sit for
// long before the page either fills in or says it is empty.
const RETRY_DELAYS_MS = [1000, 3000];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The signed-in account as the rest of the app knows it (src/index.js
// restores the same key into Redux at startup).
const currentUserId = () => {
  try {
    return JSON.parse(localStorage.getItem("user"))?.id || null;
  } catch (e) {
    return null;
  }
};

const readStored = () => {
  try {
    // Data written by an older code path (no owner recorded) or by another
    // account is treated as absent and fetched afresh.
    if (sessionStorage.getItem(OWNER_KEY) !== currentUserId()) return null;
    const school = sessionStorage.getItem(SCHOOL_KEY);
    const classroom = sessionStorage.getItem(CLASSROOM_KEY);
    if (!school || !classroom) return null;
    return { schools: JSON.parse(school), classrooms: JSON.parse(classroom) };
  } catch (e) {
    return null;
  }
};

// Empty lists are stored too: "asked, and there are none" must be told apart
// from "not asked yet", or a student without a classroom waits forever.
// Returns what was stored, so the caller does not have to read it back
// through readStored — which answers null whenever the owner check cannot
// pass (no "user" in localStorage yet), and would hand TopNavbar a null to
// destructure although the request itself succeeded.
const store = (me) => {
  const data = { schools: me?.schools || [], classrooms: me?.classrooms || [] };
  sessionStorage.setItem(SCHOOL_KEY, JSON.stringify(data.schools));
  sessionStorage.setItem(CLASSROOM_KEY, JSON.stringify(data.classrooms));
  sessionStorage.setItem(OWNER_KEY, currentUserId() || "");
  return data;
};

// Drop what is stored. For the moment the page takes a new tablet session
// without a sign-out in between (the SSO handover reloads straight into
// /mybestuser?code=): sessionStorage survives that reload, and lists stored
// under the previous token must not be read back as the new session's.
export const clearSchoolClassroomData = () => {
  try {
    sessionStorage.removeItem(SCHOOL_KEY);
    sessionStorage.removeItem(CLASSROOM_KEY);
    sessionStorage.removeItem(OWNER_KEY);
  } catch (e) {
    // storage blocked — nothing stored to drop
  }
};

let inFlight = null;

/**
 * Resolves with { schools, classrooms } once sessionStorage holds them,
 * fetching through `client` (graphql-request) when it does not. Rejects only
 * after the retries are spent; the caller then shows its empty state.
 */
export const ensureSchoolClassroomData = (
  client,
  pagination = { limit: 1 }
) => {
  const stored = readStored();
  if (stored) return Promise.resolve(stored);
  if (inFlight) return inFlight;

  inFlight = (async () => {
    let lastError = null;
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
      try {
        const data = await client.request(GetSchools, { pagination });
        // `me: null` is how mybestuser answers a credential it no longer
        // accepts (a token issued before the user's last logout), not a
        // student without a school. Storing it would pin empty lists to
        // this account for the rest of the session; fail instead, so the
        // retry (or the next page) asks again with the current token.
        if (!data?.me) {
          throw new Error("GetSchools answered no user for this credential");
        }
        return store(data.me);
      } catch (err) {
        lastError = err;
        // A rejected credential will not pass on a retry, and the client has
        // already signed the user out for it.
        if (isAuthError(err)) break;
        if (attempt < RETRY_DELAYS_MS.length) {
          console.warn(
            "[schoolClassroom] GetSchools failed, retrying:",
            err?.message || err
          );
          await sleep(RETRY_DELAYS_MS[attempt]);
        }
      }
    }
    rollbar.error("[schoolClassroom] GetSchools failed", lastError);
    throw lastError;
  })().finally(() => {
    inFlight = null;
  });

  return inFlight;
};
